// useReducer with Typescript:
// https://github.com/hswolff/youtube/blob/master/videos/why-i-love-usereducer/src/LoginUseReducerTypeScript.tsx

import React, { useReducer, ReactNode } from 'react';
import { createContext } from 'use-context-selector';
import { FileUploaded } from '@scormhero/shared';

const initialState: IUIState = {
  background: {},
  blinkNext: false,
  setBackground: () => {},
  clearBackground: () => {},
  startBlinkNext: () => {},
  stopBlinkNext: () => {},
  confirmModal: {
    isOpen: false,
  },
  showConfirm: () => console.warn('function_not_defined'),
  hideConfirm: () => console.warn('function_not_defined'),
};

type IUIState = {
  background: IBackground;
  blinkNext: boolean;
  setBackground: (background: IBackground) => void;
  clearBackground: () => void;
  startBlinkNext: () => void;
  stopBlinkNext: () => void;
  confirmModal: IConfirmModal;
  showConfirm: (confirmModal: IConfirmModal) => void;
  hideConfirm: () => void;
};

type IConfirmModal = {
  type?: 'success' | 'danger';
  title?: string | ReactNode;
  description?: string | ReactNode;
  cancelLabel?: string | ReactNode;
  confirmLabel?: string | ReactNode;
  isOpen?: boolean;
  onConfirm?: () => void;
  onClickOutside?: () => void;
  confirmClassName?: string;
  cancelClassName?: string;
};

type IBackground = {
  image?: FileUploaded;
  style?: {
    backgroundSize?: 'contain';
    backgroundPosition?: 'left' | 'center' | 'right';
    height?: '300px' | '400px';
  };
  opacity?: number;
};

type IUIAction =
  | { type: 'SET_BACKGROUND'; payload: IBackground }
  | { type: 'CLEAR_BACKGROUND' }
  | { type: 'START_BLINK_NEXT' }
  | { type: 'STOP_BLINK_NEXT' }
  | {
      type: 'SHOW_CONFIRM';
      payload: IConfirmModal;
    }
  | {
      type: 'HIDE_CONFIRM';
    };

function uiReducer(state: IUIState, action: IUIAction): IUIState {
  switch (action.type) {
    case 'SET_BACKGROUND':
      return {
        ...state,
        background: action.payload,
      };
    case 'CLEAR_BACKGROUND':
      return {
        ...state,
        background: {},
      };
    case 'START_BLINK_NEXT':
      return {
        ...state,
        blinkNext: true,
      };
    case 'STOP_BLINK_NEXT':
      return {
        ...state,
        blinkNext: false,
      };
    case 'SHOW_CONFIRM':
      return {
        ...state,
        confirmModal: { ...action.payload, isOpen: true },
      };

    case 'HIDE_CONFIRM':
      return {
        ...state,
        confirmModal: {
          // reset everything
          isOpen: false,
          type: undefined,
          title: undefined,
          description: undefined,
          cancelLabel: undefined,
          confirmLabel: undefined,
          onConfirm: undefined,
          onClickOutside: undefined,
        },
      };
    default:
      return state;
  }
}

export const UIContext = createContext(initialState as IUIState);

export default function UIProvider(props: { children?: ReactNode }) {
  const [state, dispatch] = useReducer(uiReducer, initialState);

  // Actions
  // reminder: always dispatch; otherwise, is a SELECTOR, not an ACTION
  function setBackground(background = {} as IBackground) {
    dispatch({
      type: 'SET_BACKGROUND',
      payload: background,
    });
  }

  function clearBackground() {
    dispatch({
      type: 'CLEAR_BACKGROUND',
    });
  }

  function startBlinkNext() {
    dispatch({
      type: 'START_BLINK_NEXT',
    });
  }

  function stopBlinkNext() {
    dispatch({
      type: 'STOP_BLINK_NEXT',
    });
  }

  function showConfirm(confirmModal: IConfirmModal) {
    dispatch({
      type: 'SHOW_CONFIRM',
      payload: confirmModal,
    });
  }

  function hideConfirm() {
    dispatch({
      type: 'HIDE_CONFIRM',
    });
  }

  return (
    <UIContext.Provider
      value={{
        ...state,
        setBackground,
        clearBackground,
        startBlinkNext,
        stopBlinkNext,
        showConfirm,
        hideConfirm,
      }}
    >
      {props.children}
    </UIContext.Provider>
  );
}
